import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const skills = [
  { name: 'Collaboration', desc: 'Working together across grades to plan events and solve problems as a team.' },
  { name: 'Leadership', desc: 'Stepping up to represent classmates and guide student-led initiatives with confidence.' },
  { name: 'Decision-Making', desc: 'Weighing ideas, listening to peers, and making choices that serve the whole school.' },
];

const responsibilities = [
  { name: 'Amplifying Student Voices', desc: 'Elected leaders from grades 3 to 8 make sure every student feels heard and valued.' },
  { name: 'Student-Led Activities', desc: 'Planning and running exciting events that bring energy and fun to school life.' },
  { name: 'Community Service Projects', desc: 'Leading projects that give back and make a real difference in our community.' },
  { name: 'Building School Spirit', desc: 'Working with school leadership to spark communication and foster school spirit.' },
];

const StudentCouncilPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">Student Life</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Student Council
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            An energetic team of elected leaders from grades 3 to 8, amplifying the voices of their fellow students.
          </p>
        </div>
      </section>

      {/* Section — image left, text right */}
      <section className="flex flex-col lg:flex-row min-h-[460px]">
        <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden">
          <img
            src="/assets/images/studentcouncil/IMG_0740.jpeg"
            alt="PS 96 Student Council members"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="lg:w-1/2 bg-[#00213a] flex items-center px-10 py-14 lg:px-16">
          <div className="max-w-lg">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#fdbb2e] mb-3">Elected Leaders</p>
            <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              The Driving Force Behind Student Life.
            </h2>
            <p className="text-white/80 leading-relaxed text-lg">
              Our Student Council is an energetic team of elected leaders from grades 3 to 8, dedicated to amplifying the voices of their fellow students! These young leaders aren't just representatives; they are the driving force behind exciting, student-led activities and community service projects that make our school a vibrant place. They work with school leadership to spark communication and foster school spirit, ensuring that every student feels heard and valued.
            </p>
          </div>
        </div>
      </section>

      {/* Skills callout */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/5 rounded-lg border border-[#fdbb2e]/30 mb-4">
              <span className="text-sm font-semibold text-[#00213a]">More Than a Title</span>
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 mb-4 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              Skills That Last a Lifetime
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              By participating in the Student Council, students develop essential skills — and learn the importance of teamwork and making a difference in our community.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {skills.map((item) => (
              <div key={item.name} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md hover:border-[#fdbb2e]/40 transition-all duration-200">
                <div className="w-8 h-8 rounded-lg bg-[#fdbb2e]/10 flex items-center justify-center mb-4">
                  <svg className="w-4 h-4 text-[#00213a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{item.name}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsibilities Grid */}
      <section className="bg-[#00213a] py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg border border-white/20 mb-4">
              <span className="text-sm font-semibold text-white">What They Do</span>
            </div>
            <h2 className="text-4xl font-semibold text-white tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              Leading the Way at PS 96
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {responsibilities.map((item) => (
              <div key={item.name} className="bg-white/10 border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-colors duration-200">
                <h3 className="text-base font-semibold text-white mb-2">{item.name}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-gray-900 mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            A Positive, Inclusive Community
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Together, our Student Council isn't just creating a positive and inclusive environment — they're making a difference in our community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/school-spirit"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#00213a] text-white font-semibold rounded-xl hover:bg-[#003060] transition-colors duration-200"
            >
              Explore School Spirit <ArrowRightIcon />
            </a>
            <a
              href="/contact-us"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#00213a] text-[#00213a] font-semibold rounded-xl hover:bg-[#00213a] hover:text-white transition-colors duration-200"
            >
              Contact Us <ArrowRightIcon />
            </a>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default StudentCouncilPage;
